'use client';
import DevClickOpener from '@repo/ui/src/components/DevClickOpener';
import { useOnRouteChange } from '@hooks/useOnRouteChange';
import { analytics } from '@repo/ui/src/utils/analytics';
import { identify } from '@repo/ui/src/utils/identify';
import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import { removeQueryParam } from '@utils/removeQueryParam';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import Cookies from 'js-cookie';
import { ReadonlyURLSearchParams, usePathname, useRouter } from 'next/navigation';
import React from 'react';
/// <reference types="../src/types/window" />

// Declare window.identify for Vercel build compatibility
declare global {
  interface Window {
    identify: (email: string) => Promise<void>;
  }
}

gsap.registerPlugin(ScrollTrigger);

export interface TemplateProps {
  children: React.ReactNode;
}

const { segment } = publicRuntimeConfig;
const Template = ({ children }: TemplateProps) => {
  const isProd = process.env.NEXT_PUBLIC_APP_ENV === 'production';
  const { refresh } = useRouter();
  const previousUrl = React.useRef<string>('');
  const pathname = usePathname();

  React.useEffect(() => {
    window.identify = async (email: string) => {
      identify({ email });
    };
    withOneTrust(analytics).load({ writeKey: segment.writeKey! });
    (window.analytics as any) = analytics;
    previousUrl.current = `${window.location.host}${pathname}`;
    // fix referrer on client side routing
    window.__ANALYTICS_REFERRER__ = document.referrer;
  }, [pathname]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('basket')) {
      if (!Cookies.get('isBasketLink')) {
        return refresh();
      }
      removeQueryParam(searchParams as ReadonlyURLSearchParams, 'basket');
    }
  }, [refresh]);

  // fix OneTrust banner stealing click focus
  React.useEffect(() => {
    const originalStopPropagation = Event.prototype.stopPropagation;
    Event.prototype.stopPropagation = function () {
      const stack = new Error().stack;

      if (stack?.includes('onClickCloseBanner')) {
        return;
      }

      originalStopPropagation.apply(this);
    };

    return () => {
      Event.prototype.stopPropagation = originalStopPropagation;
    };
  }, []);

  useOnRouteChange(() => {
    window.__ANALYTICS_REFERRER__ = previousUrl.current;
    previousUrl.current = `${window.location.host}${pathname}`;
  });

  return <>
  {!isProd &&
    <DevClickOpener />
  }
    {children}
  </>;
};

export default Template;
